/*
  Aligne le fond et le masque de coque des cartes produit sur le modèle sélectionné.

  Pourquoi côté client : sur une collection ou une recherche, le modèle est lisible côté
  serveur dans les valeurs de filtre actives (cf. snippets/selected-phone-model-handle.liquid),
  mais les listings sans facets — accueil, featured-collection, recommandations, shop-the-look —
  n'ont aucun filtre. Le seul porteur de la sélection y est le cookie `phone_model`,
  inaccessible à Liquid.

  Pourquoi ça échappe au plafond des 50 : on n'interroge PAS la liste `modeles_compatibles`
  du produit (Liquid n'en expose que 50 entrées sur ~500, et la Storefront API demanderait
  une pagination par curseur). On interroge un seul métaobjet par son GID. Le TYPE de coque,
  lui, vient du serveur via `data-fond-field` / `data-masque-field` : c'est une donnée produit,
  et elle ne varie pas d'un modèle à l'autre.
*/
(() => {
  const COOKIE_NAME = 'phone_model';
  const FILTER_PARAM = 'filter.p.m.custom.modeles_compatibles';
  const CARD_SELECTOR = '[data-model-mask]';
  const BG_SELECTOR = '.product-card__compatible-background-img';
  const MASK_SELECTOR = '.product-card__compatible-mask img';
  const CACHE_PREFIX = 'listing-model-mask:';
  const CACHE_TTL_MS = 60 * 60 * 1000;
  const IMAGE_WIDTH = 1080;

  const inFlight = new Map();

  function readCookieSelection() {
    const match = document.cookie.match(`(^|;)\\s*${COOKIE_NAME}\\s*=\\s*([^;]+)`);
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch {
      return null;
    }
  }

  // Repli sur le filtre de l'URL quand il n'y a pas de cookie : une URL filtrée
  // partagée ou mise en favori arrive sans sélection mémorisée.
  function readUrlSelection() {
    try {
      const value = new URLSearchParams(window.location.search).get(FILTER_PARAM);
      return value && value.startsWith('gid://') ? { id: value } : null;
    } catch {
      return null;
    }
  }

  function handleize(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /*
   * Une coque n'existe que pour la marque du produit : appliquer le masque d'un modèle
   * d'une autre marque produit un rendu faux (masque iPhone sur une coque Oppo). Ça arrive
   * dès qu'on visite une collection de marque sans que le modèle en cookie y corresponde.
   *
   * Le contrôle est volontairement permissif : sans marque connue des deux côtés on
   * applique quand même — accessoires universels, et repli sur le filtre de l'URL qui ne
   * porte que le GID, sans la marque.
   */
  function brandAllows(card, selection) {
    const cardBrand = handleize(card.dataset.modelBrand);
    const modelBrand = handleize(selection?.marque);
    if (!cardBrand || !modelBrand) return true;
    return cardBrand === modelBrand;
  }

  function storefrontToken() {
    return document.querySelector('[data-storefront-token]')?.dataset.storefrontToken || '';
  }

  function sized(url) {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${IMAGE_WIDTH}`;
  }

  function readCache(gid) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + gid) || 'null');
      if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.map;
    } catch { /* quota ou JSON cassé — on refetch */ }
    return null;
  }

  function writeCache(gid, map) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + gid, JSON.stringify({ ts: Date.now(), map }));
    } catch { /* quota — ignore */ }
  }

  // Un seul métaobjet, donc pas de pagination : `node(id:)` suffit.
  async function fetchModelImages(gid) {
    const cached = readCache(gid);
    if (cached) return cached;
    if (inFlight.has(gid)) return inFlight.get(gid);

    const token = storefrontToken();
    if (!token) return null;

    const query = `
      query ModelMasks($id: ID!) {
        node(id: $id) {
          ... on Metaobject {
            fields {
              key
              reference { ... on MediaImage { image { url } } }
            }
          }
        }
      }
    `;

    const promise = (async () => {
      try {
        const response = await fetch('/api/2024-10/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query, variables: { id: gid } }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const payload = await response.json();
        if (payload.errors) throw new Error(`GraphQL errors: ${JSON.stringify(payload.errors)}`);

        const fields = payload?.data?.node?.fields;
        if (!fields) return null;

        const map = {};
        for (const field of fields) {
          const url = field?.reference?.image?.url;
          if (url) map[field.key] = url;
        }
        writeCache(gid, map);
        return map;
      } catch (error) {
        console.warn('[listing-model-mask] fetch failed:', error.message);
        return null;
      } finally {
        inFlight.delete(gid);
      }
    })();

    inFlight.set(gid, promise);
    return promise;
  }

  function swap(img, url) {
    if (!img || !url) return;
    const next = sized(url);
    if (img.dataset.modelMaskApplied === next) return;
    // `srcset` retiré, sinon le navigateur garde un candidat de l'ancien modèle.
    img.removeAttribute('srcset');
    img.src = next;
    img.dataset.modelMaskApplied = next;
  }

  function pendingCards(root) {
    const scope = root instanceof Element ? root : document;
    const cards = [...scope.querySelectorAll(CARD_SELECTOR)];
    if (scope !== document && scope.matches?.(CARD_SELECTOR)) cards.push(scope);
    return cards;
  }

  async function apply(root = document) {
    const selection = readCookieSelection();
    const gid = selection?.id || readUrlSelection()?.id || null;
    if (!gid) return;

    // Le cookie porte le GID complet, `system.id` côté Liquid ne rend que le nombre :
    // on compare les identifiants numériques, sinon la garde ne matcherait jamais et
    // les listings filtrés seraient repeints pour rien.
    const modelKey = String(gid).split('/').pop();

    // Cartes déjà rendues avec le bon modèle côté serveur, ou d'une autre marque : on passe.
    const cards = pendingCards(root)
      .filter((card) => card.dataset.modelId !== modelKey)
      .filter((card) => brandAllows(card, selection));
    if (!cards.length) return;

    const images = await fetchModelImages(gid);
    if (!images) return;

    for (const card of cards) {
      const fondUrl = images[card.dataset.fondField];
      const maskUrl = images[card.dataset.masqueField];
      swap(card.querySelector(BG_SELECTOR), fondUrl);
      swap(card.querySelector(MASK_SELECTOR), maskUrl);
      if (fondUrl || maskUrl) card.dataset.modelId = modelKey;
    }
  }

  // Les listings se re-rendent en AJAX (facettes, défilement infini, quick view,
  // recommandations). `apply` est idempotent, on peut donc l'appeler large.
  let frame = null;
  function scheduleApply(root) {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = null;
      apply(root);
    });
  }

  function observe() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR)) {
            scheduleApply(document);
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    if (!document.querySelector(CARD_SELECTOR)) {
      // Aucune carte à coque sur cette page : on observe quand même, les
      // recommandations et le quick view arrivent après coup.
      observe();
      return;
    }
    apply(document);
    observe();
  }

  document.addEventListener('phone-model:change', () => {
    document.querySelectorAll(`${CARD_SELECTOR}`).forEach((card) => {
      delete card.dataset.modelId;
    });
    scheduleApply(document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
